/**
 * PapooseCarrier — Hoodie Papoose kitten-carry mechanic (canon book p.13).
 *
 * Lifecycle:
 *   1. Starts inactive (`isWorn` = false). Minna looks normal.
 *   2. When the Apex Jigsaw solves, the owning scene / puzzle calls
 *      `activate()`. The empty `garment_carrier.glb` prop attaches as a child
 *      of Minna's mesh (parented transform) — she *wears* it.
 *   3. While worn, calmed kittens in range can be "loaded" via
 *      `tryLoadKitten(...)` (driven by Minna holding Purr near a calmed
 *      kitten). Loaded kittens parent to Minna's mesh with a per-slot
 *      back-offset and their behaviour state is forced to `following`
 *      so they don't drift off.
 *   4. Carried kittens are "safe" — they do NOT re-frantic if Minna
 *      jumps or falls. The pen treats them as normal calmed kittens
 *      once the carrier enters pen radius: `unloadAll()` detaches and
 *      places them at the pen position so `KittenPen.penKitten()`
 *      counts them.
 *
 * Design notes:
 *   - The carrier exposes a narrow public surface (`isWorn`, carried
 *     kitten list, `tryLoadKitten`, `unloadAll`, `update`) so puzzle
 *     modules don't reach into private fields for testing.
 *   - The GLB load is fire-and-forget; the carrier is functional
 *     (can load/carry/unload) even if the mesh fails to load — the
 *     gameplay state still advances, we just lose the visual.
 *   - Tests can inject their own load state via `activate({ skipModel:
 *     true })` so they don't need an AssetManager to exercise carry
 *     logic.
 */
import { Scene } from "@babylonjs/core/scene";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { getAssetManager, InstantiatedModel } from "../../systems/AssetManager";
import type { Kitten } from "../npcs/Kitten";
import { tagAs } from "../../levels/PlaceholderTag";
import type { Light } from "@babylonjs/core/Lights/light";

/** Maximum kittens the papoose can hold at once. */
export const PAPOOSE_MAX_CAPACITY = 2;

/** Radius (world units) inside which Minna can load a nearby calmed kitten. */
export const PAPOOSE_LOAD_RADIUS = 1.6;

/**
 * Per-slot back offset applied to carried kittens relative to Minna's
 * mesh. Two slots so the second kitten doesn't clip through the first.
 * Y is slightly above Minna's shoulder; Z points behind her.
 */
const PAPOOSE_SLOT_OFFSETS: ReadonlyArray<Vector3> = [
  new Vector3(0, 0.55, -0.35),
  new Vector3(0, 0.90, -0.40),
];

/** Options for `activate()`. */
export interface PapooseActivateOptions {
  /**
   * If true, skip the GLB load. Used by unit tests so the carrier's
   * gameplay logic can run without the AssetManager / network path.
   */
  skipModel?: boolean;
}

export class PapooseCarrier {
  private readonly scene: Scene;
  private readonly minnaMesh: AbstractMesh;

  /** True once `activate()` has flipped the papoose on. */
  private worn = false;

  /** GLB instance parented to Minna's mesh. Null until load completes. */
  private papooseModel: InstantiatedModel | null = null;
  private selfLight: Light | null = null;
  private selfLightExclusions: AbstractMesh[] = [];

  /**
   * Carried kittens in insertion order. Position in the array maps
   * directly to `PAPOOSE_SLOT_OFFSETS` so kitten 0 rides the lower
   * slot, kitten 1 rides the higher. `releaseAll()` clears this list.
   */
  private carried: Kitten[] = [];

  constructor(scene: Scene, minnaMesh: AbstractMesh) {
    this.scene = scene;
    this.minnaMesh = minnaMesh;
  }

  /** Whether Minna is currently wearing the papoose. */
  get isWorn(): boolean {
    return this.worn;
  }

  /** Whether the papoose is at capacity. */
  get isFull(): boolean {
    return this.carried.length >= PAPOOSE_MAX_CAPACITY;
  }

  /** Snapshot of currently carried kittens (shallow copy). */
  get carriedKittens(): ReadonlyArray<Kitten> {
    return this.carried.slice();
  }

  /**
   * Activate the papoose. Loads the GLB as a child of Minna's mesh and
   * emits `HOODIE_PAPOOSE_READY`. Idempotent — calling twice is a no-op.
   */
  activate(options: PapooseActivateOptions = {}): void {
    if (this.worn) return;
    this.worn = true;

    gameEvents.emit(GameEvents.HOODIE_PAPOOSE_READY);

    if (!options.skipModel) {
      this.loadModel();
    }
  }

  /**
   * Try to load a calmed kitten into the papoose. Returns true on
   * success. Failure modes:
   *   - papoose not worn
   *   - already at capacity
   *   - kitten is not calm/following, already penned, or already carried
   *   - kitten is outside `PAPOOSE_LOAD_RADIUS` of Minna
   */
  tryLoadKitten(kitten: Kitten): boolean {
    if (!this.worn) return false;
    if (this.isFull) return false;
    if (kitten.isPenned) return false;
    if (this.carried.includes(kitten)) return false;
    const isCalmOrFollowing =
      kitten.behavior.isCalmed || kitten.behavior.state === "following";
    if (!isCalmOrFollowing) return false;

    const dx = kitten.mesh.position.x - this.minnaMesh.position.x;
    const dy = kitten.mesh.position.y - this.minnaMesh.position.y;
    const dz = kitten.mesh.position.z - this.minnaMesh.position.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq > PAPOOSE_LOAD_RADIUS * PAPOOSE_LOAD_RADIUS) return false;

    // Parent the kitten's mesh to Minna so she can jump / move freely
    // and the kitten rides on her back. We keep the kitten's behavior
    // in `calmed` so the pen's "is calmed?" check still passes on
    // unload; `following` would move the kitten AWAY from Minna when
    // she walks, which is the opposite of "carried."
    kitten.mesh.parent = this.minnaMesh;
    const slot = PAPOOSE_SLOT_OFFSETS[
      Math.min(this.carried.length, PAPOOSE_SLOT_OFFSETS.length - 1)
    ];
    kitten.mesh.position = slot.clone();
    kitten.setCarrierSlot(this.carried.length);

    this.carried.push(kitten);

    gameEvents.emit(GameEvents.HOODIE_PAPOOSE_KITTEN_LOADED, {
      npcKey: kitten.npcKey,
    });

    return true;
  }

  /**
   * Unload every carried kitten at the given world position (typically
   * the pen's root). Each kitten is re-parented to the scene root, its
   * world position set to `worldPos` (with a tiny per-slot X offset so
   * they don't all stack on the same point), and `HOODIE_PAPOOSE_KITTEN_UNLOADED`
   * fires for each.
   *
   * Returns the kittens that were unloaded so the caller can feed them
   * into `KittenPen.penKitten()` / `kitten.pen()` sequentially.
   */
  unloadAll(worldPos: Vector3, reason: "pen" | "manual" | "dispose" = "pen"): Kitten[] {
    if (this.carried.length === 0) return [];
    const unloaded = this.carried.slice();
    for (let i = 0; i < unloaded.length; i++) {
      const kitten = unloaded[i];
      // Detach from Minna. Babylon parenting preserves world transform
      // when we set parent to null BEFORE overwriting position, so we
      // null the parent first, then place the kitten at worldPos.
      kitten.mesh.parent = null;
      kitten.mesh.position = new Vector3(
        worldPos.x + (i - (unloaded.length - 1) / 2) * 0.35,
        worldPos.y,
        worldPos.z,
      );
      kitten.setCarrierSlot(null);
      gameEvents.emit(GameEvents.HOODIE_PAPOOSE_KITTEN_UNLOADED, {
        npcKey: kitten.npcKey,
        reason,
      });
    }
    this.carried.length = 0;
    return unloaded;
  }

  /**
   * Per-frame hook. The only live thing the papoose does each frame is
   * assert that carried kittens still report as calmed — if any have
   * somehow drifted out of that state (Astral Miss, purr timeout, etc.)
   * they are re-calmed in place so a "carried" kitten never reverts
   * to frantic mid-carry.
   */
  update(): void {
    if (!this.worn) return;
    for (const kitten of this.carried) {
      if (!kitten.behavior.isCalmed) {
        kitten.behavior.setState("calmed");
      }
    }
  }

  /** Tear down — drop carried kittens in place and dispose the GLB. */
  dispose(): void {
    if (this.carried.length > 0) {
      this.unloadAll(this.minnaMesh.position, "dispose");
    }
    if (this.selfLight) {
      this.selfLight.excludedMeshes = this.selfLight.excludedMeshes.filter(
        (mesh) => !this.selfLightExclusions.includes(mesh),
      );
      this.selfLight = null;
      this.selfLightExclusions = [];
    }
    if (this.papooseModel) {
      this.papooseModel.dispose();
      this.papooseModel = null;
    }
    this.worn = false;
  }

  /**
   * Fire-and-forget GLB load. Failure is silent — the carrier remains
   * functional without the visual (see class docstring).
   */
  private async loadModel(): Promise<void> {
    try {
      const instance = await getAssetManager().instantiateModel(
        this.scene,
        "/assets/models/environment/garment_carrier.glb",
        undefined,
        // Mesh clones allow per-wearer light filtering without changing the
        // cached source used by the display. Materials remain shared.
        { cloneMaterials: false, doNotInstantiate: true },
      );
      if (!this.worn) {
        // Activation was undone (or the scene disposed) while the model
        // was loading. Dispose to avoid an orphan mesh.
        instance.dispose();
        return;
      }
      this.papooseModel = instance;
      for (const root of instance.rootNodes) {
        if (root instanceof TransformNode) {
          root.parent = this.minnaMesh;
          root.position = new Vector3(0, 0.25, -0.2);
          root.scaling.scaleInPlace(0.55);
          root.metadata = { ...root.metadata, catsArtRole: "worn-carrier" };
          tagAs(root, "final");
        }
      }
      for (const mesh of instance.meshes) {
        mesh.isPickable = false;
        tagAs(mesh, "final");
      }
      // This unshadowed point light sits inside Minna. It should not shine
      // through her body and turn the linen on her back into green plastic.
      // Keep the rim light, eye glow and illumination of nearby objects.
      this.selfLight = this.scene.getLightByName("minnaLight");
      if (this.selfLight) {
        this.selfLightExclusions = instance.meshes.filter((mesh) => mesh.getTotalVertices() > 0);
        this.selfLight.excludedMeshes.push(...this.selfLightExclusions);
      }
    } catch (err) {
      console.warn("[PapooseCarrier] Failed to load garment_carrier.glb:", err);
    }
  }
}
